import React, { useState } from 'react';
import api from '../../services/api';

/**
 * Buttons in ConversationThread header for taking over from AI or releasing back to AI.
 */
// AiIntentService raises these reason codes when handing off — surface a
// human-readable label so staff knows WHY the AI bailed instead of just
// seeing "Escalated - needs attention" for every reason.
const ESCALATION_REASON_LABELS = {
  LOW_CONFIDENCE: 'low intent confidence',
  UNCLEAR_INTENT: 'unclear intent',
  CLINICAL_QUESTION: 'clinical question (PHI safeguard)',
  HUMAN_REQUESTED: 'patient asked for a human',
  CANCEL_REQUEST: 'cancellation request',
  COMPLAINT: 'patient complaint',
  NO_BOOKING_LINK: 'no booking link configured',
  AMBIGUOUS_INTENT: 'ambiguous intent',
  REFILL_REQUEST: 'refill request',
  RESULTS_REQUEST: 'results request',
  BILLING_QUESTION: 'billing question',
  QUESTION_NEEDS_HUMAN: 'question the AI could not answer',
};
function formatEscalationReason(reason) {
  return ESCALATION_REASON_LABELS[reason] || reason.toLowerCase().replace(/_/g, ' ');
}

function AiTakeoverControls({ practiceId, subConversation, onStatusChange }) {
  const [loading, setLoading] = useState(false);

  if (!subConversation?.ai_enabled) return null;

  const isHumanTakeover = subConversation.ai_takeover_status === 'human_takeover';
  const isEscalated = subConversation.ai_takeover_status === 'escalated';
  const isAiActive = subConversation.ai_takeover_status === 'ai_active';

  const handleTakeover = async () => {
    setLoading(true);
    try {
      const result = await api.aiTakeover(practiceId, subConversation.id);
      if (result.success && onStatusChange) {
        onStatusChange(result.data);
      }
    } catch (err) {
      console.error('Takeover error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async () => {
    setLoading(true);
    try {
      const result = await api.aiReleaseTakeover(practiceId, subConversation.id);
      if (result.success && onStatusChange) {
        onStatusChange(result.data);
      }
    } catch (err) {
      console.error('Release error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {(isAiActive || isEscalated) && (
        <button
          onClick={handleTakeover}
          disabled={loading}
          style={styles.takeoverButton}
        >
          {loading ? '...' : 'Take Over'}
        </button>
      )}
      {isHumanTakeover && (
        <button
          onClick={handleRelease}
          disabled={loading}
          style={styles.releaseButton}
        >
          {loading ? '...' : 'Release to AI'}
        </button>
      )}
      <span style={styles.statusText}>
        {isAiActive && 'AI is responding'}
        {isEscalated && `Escalated${subConversation.escalation_reason ? ` — ${formatEscalationReason(subConversation.escalation_reason)}` : ' - needs attention'}`}
        {isHumanTakeover && 'You are responding'}
      </span>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 0'
  },
  takeoverButton: {
    padding: '4px 12px',
    borderRadius: '4px',
    border: '1px solid #f59e0b',
    background: '#fffbeb',
    color: '#92400e',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  releaseButton: {
    padding: '4px 12px',
    borderRadius: '4px',
    border: '1px solid #10b981',
    background: '#ecfdf5',
    color: '#065f46',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  statusText: {
    fontSize: '12px',
    color: '#6b7280'
  }
};

export default AiTakeoverControls;
